import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Heart, ExternalLink } from "lucide-react";

/* ═══ DESIGN TOKENS (synced with MarkerCollection) ═══ */
const C = {
  bg: "#070707", bg2: "#11100E", card: "#181614",
  text: "#F5F2EB", text2: "#B5B0A7", gold: "#C9A055",
  bSub: "rgba(245,242,235,0.10)",
};
const F = {
  brush: "'Permanent Marker', cursive",
  cond: "'Saira Condensed', sans-serif",
  hand: "'Caveat', cursive",
  sans: "'DM Sans', 'Outfit', sans-serif",
};
const GREEN = "#4ade80";

/* ═══ VISIBILITY HOOK ═══ */
function useVisible(threshold = 0.15) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, v];
}

/* ═══ ANIMATED COUNTER ═══ */
function AnimatedCounter({ target, prefix = "$", duration = 2500, visible }) {
  const [val, setVal] = useState(0);
  const done = useRef(false);
  useEffect(() => {
    if (!visible || done.current) return;
    done.current = true;
    const steps = 80;
    const inc = target / steps;
    let cur = 0;
    const iv = setInterval(() => {
      cur += inc;
      if (cur >= target) { setVal(target); clearInterval(iv); }
      else setVal(cur);
    }, duration / steps);
    return () => clearInterval(iv);
  }, [visible, target, duration]);
  return <>{prefix}{val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>;
}

export default function CharityImpactDashboard() {
  useSEO({ title: "Art for a Cause — Impact Dashboard", description: "See how much money Simple Truths Club marker art has raised for nonprofits." });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    http.get("/marker-collection/public/charity-impact")
      .then(r => setData(r.data))
      .catch(() => setData({ grand_total: 0, nonprofit_count: 0, total_artworks: 0, total_prints_sold: 0, leaderboard: [] }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (!loading && data) setLoaded(true); }, [loading, data]);

  if (loading) return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
        <div className="animate-pulse text-sm" style={{ fontFamily: F.sans, color: C.text2 }}>Loading impact data...</div>
      </div>
    </Layout>
  );

  const { grand_total, nonprofit_count, total_artworks, total_prints_sold, leaderboard } = data;

  return (
    <Layout>
      <div className="min-h-screen" style={{ background: C.bg, color: C.text }}>
        <style>{`
          @keyframes mc-grain { 0%,100% { transform: translate(0,0); } }
          @keyframes mc-shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
          .mc-grit { position: relative; }
          .mc-grit::before { content:''; position:absolute; inset:0; width:100%; height:100%; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"); pointer-events:none; z-index:0; }
          .mc-shimmer-green { background: linear-gradient(90deg, ${GREEN}, #86efac, ${GREEN}); background-size: 200% auto; animation: mc-shimmer 3s linear infinite; }
        `}</style>

        {/* ─── Back nav ─── */}
        <div className="mc-grit overflow-hidden" style={{ background: C.bg2, borderBottom: `1px solid ${C.bSub}` }}>
          <div className="max-w-7xl mx-auto px-6 py-4 relative z-10">
            <Link to="/marker-collection" className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase hover:opacity-80 transition-opacity"
              style={{ fontFamily: F.sans, color: C.text2 }} data-testid="impact-back">
              <ArrowLeft size={14} /> Back to Collection
            </Link>
          </div>
        </div>

        {/* Shimmer green line */}
        <div className="h-[2px] mc-shimmer-green" />

        {/* ─── Hero ─── */}
        <section className="mc-grit overflow-hidden relative" style={{ background: "#070a07" }} data-testid="impact-hero">
          <div className="absolute top-[-80px] left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-[0.06] pointer-events-none" style={{ background: `radial-gradient(circle, ${GREEN} 0%, transparent 70%)` }} />

          <div className="max-w-5xl mx-auto px-6 py-20 md:py-28 text-center relative z-10">
            <div className={`transition-all duration-700 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase font-bold px-5 py-2 mb-6"
                style={{ fontFamily: F.sans, background: `${GREEN}12`, color: GREEN, border: `1px solid ${GREEN}30` }}>
                <Heart size={12} fill={GREEN} /> ART FOR A CAUSE
              </span>

              <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight leading-[0.9] mb-4"
                style={{ fontFamily: F.cond }}>
                REAL ART.<br />
                <span style={{ color: GREEN }}>REAL IMPACT.</span>
              </h1>

              <p className="text-lg mb-12 max-w-xl mx-auto" style={{ fontFamily: F.hand, color: `${GREEN}90` }}>
                Every mark makes a difference. Here's the proof.
              </p>

              {/* Big stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                {[
                  ["TOTAL RAISED", <AnimatedCounter target={grand_total} visible={loaded} />, true],
                  ["NONPROFITS HELPED", nonprofit_count, false],
                  ["ARTWORKS DONATED", total_artworks, false],
                  ["PRINTS SOLD", total_prints_sold, false],
                ].map(([label, value, isBig], i) => (
                  <div key={label} className={`py-5 px-4 transition-all duration-500 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
                    style={{ transitionDelay: `${400 + i * 100}ms`, background: `${GREEN}06`, border: `1px solid ${GREEN}18` }}>
                    <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ fontFamily: F.sans, color: `${GREEN}70` }}>{label}</div>
                    <div className={`font-black ${isBig ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"}`}
                      style={{ fontFamily: F.cond, color: GREEN }} data-testid={`impact-stat-${i}`}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="h-[2px] mc-shimmer-green" />

        {/* ─── Leaderboard ─── */}
        <section className="mc-grit overflow-hidden" style={{ background: C.bg }} data-testid="impact-leaderboard">
          <div className="max-w-5xl mx-auto px-6 py-16 md:py-24 relative z-10">
            <div className={`text-center mb-12 transition-all duration-700 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-2" style={{ fontFamily: F.cond }}>
                THE ORGANIZATIONS WE SUPPORT
              </h2>
              <p className="text-sm" style={{ fontFamily: F.sans, color: C.text2 }}>
                Ranked by total funds raised through art
              </p>
            </div>

            {leaderboard.length === 0 ? (
              <div className={`text-center py-16 transition-all duration-700 ${loaded ? "opacity-100" : "opacity-0"}`}
                style={{ background: `${GREEN}04`, border: `1px solid ${GREEN}10` }}>
                <Heart size={40} className="mx-auto mb-4" style={{ color: `${GREEN}30` }} />
                <p className="text-lg font-bold mb-2" style={{ fontFamily: F.cond, color: C.text }}>COMING SOON</p>
                <p className="text-sm max-w-md mx-auto" style={{ fontFamily: F.sans, color: C.text2 }}>
                  We're partnering with our first nonprofits now. Check back soon to see the impact.
                </p>
                <Link to="/marker-collection#cause" className="inline-flex items-center gap-2 mt-6 px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold"
                  style={{ fontFamily: F.sans, background: GREEN, color: C.bg }} data-testid="impact-apply-link">
                  APPLY YOUR NONPROFIT
                </Link>
              </div>
            ) : (
              <div className="space-y-6">
                {leaderboard.map((np, i) => (
                  <div key={np.nonprofit_name}
                    className={`transition-all duration-600 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                    style={{ transitionDelay: `${200 + i * 120}ms`, background: `${GREEN}04`, border: `1px solid ${GREEN}12` }}
                    data-testid={`impact-nonprofit-${i}`}>

                    {/* Nonprofit header */}
                    <div className="p-6 flex items-start gap-5">
                      {/* Rank */}
                      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center text-xl font-black"
                        style={{ fontFamily: F.cond, background: i === 0 ? GREEN : `${GREEN}15`, color: i === 0 ? C.bg : GREEN }}>
                        #{i + 1}
                      </div>

                      {/* Logo */}
                      {np.nonprofit_logo && (
                        <img src={resolveImage(np.nonprofit_logo)} alt={np.nonprofit_name}
                          className="w-14 h-14 object-contain flex-shrink-0 rounded" />
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <h3 className="text-xl font-black" style={{ fontFamily: F.cond, color: C.text }}>{np.nonprofit_name}</h3>
                          {np.nonprofit_url && (
                            <a href={np.nonprofit_url.startsWith("http") ? np.nonprofit_url : `https://${np.nonprofit_url}`}
                              target="_blank" rel="noreferrer" className="text-xs hover:underline flex items-center gap-1"
                              style={{ fontFamily: F.sans, color: GREEN }}>
                              <ExternalLink size={11} /> Visit
                            </a>
                          )}
                        </div>
                        {np.nonprofit_description && (
                          <p className="text-sm leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>{np.nonprofit_description}</p>
                        )}
                      </div>

                      {/* Total */}
                      <div className="flex-shrink-0 text-right">
                        <div className="text-[10px] tracking-[0.2em] uppercase mb-1" style={{ fontFamily: F.sans, color: `${GREEN}60` }}>RAISED</div>
                        <div className="text-3xl font-black" style={{ fontFamily: F.cond, color: GREEN }}>
                          ${np.total_raised.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-[10px] mt-1" style={{ fontFamily: F.sans, color: C.text2 }}>
                          {np.artworks_count} artwork{np.artworks_count !== 1 ? "s" : ""} · {np.total_prints_sold} prints sold
                        </div>
                      </div>
                    </div>

                    {/* Artworks row */}
                    {np.artworks.length > 0 && (
                      <div className="px-6 pb-5 pt-0">
                        <div className="flex gap-3 overflow-x-auto pb-2">
                          {np.artworks.map(aw => (
                            <Link key={aw.id} to={`/marker-collection/${aw.slug}`}
                              className="flex-shrink-0 group" data-testid={`impact-artwork-${aw.slug}`}>
                              <div className="w-20 h-20 overflow-hidden mb-1.5" style={{ background: C.bg2, border: `1px solid ${C.bSub}` }}>
                                {aw.main_image ? (
                                  <img src={resolveImage(aw.main_image)} alt={aw.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Heart size={16} style={{ color: `${GREEN}30` }} />
                                  </div>
                                )}
                              </div>
                              <p className="text-[10px] truncate w-20 group-hover:text-white transition-colors" style={{ fontFamily: F.sans, color: C.text2 }}>{aw.title}</p>
                              <p className="text-[9px] font-bold" style={{ fontFamily: F.sans, color: GREEN }}>${aw.total_raised.toFixed(0)} raised</p>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* CTA */}
            <div className={`mt-16 text-center transition-all duration-700 delay-500 ${loaded ? "opacity-100" : "opacity-0"}`}>
              <p className="text-lg mb-2" style={{ fontFamily: F.hand, color: `${GREEN}70` }}>
                "Art should do something bigger than hang on a wall."
              </p>
              <p className="text-xs mb-6" style={{ fontFamily: F.sans, color: C.text2 }}>— Mick</p>
              <Link to="/marker-collection#cause"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-xs tracking-[0.25em] uppercase font-bold transition-colors hover:brightness-110"
                style={{ fontFamily: F.sans, background: GREEN, color: C.bg }}
                data-testid="impact-apply-cta">
                APPLY YOUR NONPROFIT
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
